import type { MenuItemDef } from 'ag-grid-community';
import type { CustomMenuItemProps as AgGridCustomMenuItemProps } from 'ag-grid-react';
import { useGridMenuItem } from 'ag-grid-react';
import type { ReactNode } from 'react';
import { IconName } from '../Icons';

/**
 * Extra params passed via AG Grid `MenuItemDef.menuItemParams`.
 * AG Grid merges these onto the component as top-level props.
 *
 * `label` may add presentation (e.g. bold) but must have text equivalent to `name`.
 */
export interface AgGridCustomMenuItemParams {
  iconName?: IconName;
  label: ReactNode;
}

export type AgGridCustomMenuItemComponentProps = AgGridCustomMenuItemProps & AgGridCustomMenuItemParams;

/**
 * Typed options for {@link agGridCustomMenuItem}.
 * Native AG Grid fields stay top-level; presentation extras (`iconName`, `label`) are
 * also top-level so callers never assemble `menuItem` / `menuItemParams` by hand.
 *
 * Both `name` and `label` are required:
 * - `name` — AG Grid's plain-text item name (def identity; do not put HTML here)
 * - `label` — visible React content (e.g. bolding an id); must match `name`
 *
 * `label` is what's rendered, tracked, and matched in tests
 *
 * Pass `agGridCustomMenuItem<Order>({ ... })` when action params need a row type.
 */
export type AgGridCustomMenuItemDefOptions<TData = any> = Omit<
  MenuItemDef<TData>,
  'menuItem' | 'menuItemParams' | 'icon'
> &
  AgGridCustomMenuItemParams;

/** Precise {@link MenuItemDef} returned by {@link agGridCustomMenuItem}. */
export type AgGridCustomMenuItemDef<TData = any> = Omit<MenuItemDef<TData>, 'menuItem' | 'menuItemParams' | 'icon'> & {
  menuItem: typeof AgGridCustomMenuItem;
  menuItemParams: AgGridCustomMenuItemParams;
};

/**
 * Build a {@link MenuItemDef} that renders via {@link AgGridCustomMenuItem}.
 * Callers must pass `name` and `label` (plus optional `iconName`);
 * this factory owns the AG Grid custom-component wiring.
 */
export function agGridCustomMenuItem<TData = any>({
  iconName,
  label,
  ...menuItemDef
}: AgGridCustomMenuItemDefOptions<TData>): AgGridCustomMenuItemDef<TData> {
  return {
    ...menuItemDef,
    menuItem: AgGridCustomMenuItem,
    menuItemParams: {
      label,
      ...(iconName !== undefined ? { iconName } : {}),
    },
  };
}

/**
 * Default-looking AG Grid menu row that can render a Kyoko icon and a rich React label.
 * AG Grid owns selection, focus, keyboard, submenu lifecycle, and ARIA when
 * `configureDefaults` returns true — this component only supplies presentation.
 *
 * Prefer {@link agGridCustomMenuItem} at call sites. AG Grid still receives `name`
 * on the menu def; this component renders `label`. Tracking does not read `name`.
 *
 * @see https://www.ag-grid.com/react-data-grid/component-menu-item/
 */
export function AgGridCustomMenuItem({
  iconName,
  label,
  shortcut,
  checked,
  subMenu,
  api,
}: AgGridCustomMenuItemComponentProps) {
  useGridMenuItem({
    configureDefaults: () => true,
  });

  const isRtl = api?.getGridOption('enableRtl') === true;
  // AG Grid default submenu pointer icons (subMenuOpen / subMenuOpenRtl).
  const submenuPointerIcon = isRtl ? 'small-left' : 'small-right';

  return (
    <div>
      <span className="ag-menu-option-part ag-menu-option-icon" aria-hidden="true">
        {checked ? (
          <i className={`ag-icon ${IconName.Check}`} />
        ) : iconName ? (
          <i className={`ag-icon ${iconName}`} />
        ) : null}
      </span>
      <span className="ag-menu-option-part ag-menu-option-text">{label}</span>
      <span className="ag-menu-option-part ag-menu-option-shortcut">{shortcut}</span>
      <span className="ag-menu-option-part ag-menu-option-popup-pointer" aria-hidden="true">
        {subMenu ? <span className={`ag-icon ag-icon-${submenuPointerIcon}`} role="presentation" /> : null}
      </span>
    </div>
  );
}
